import React from 'react'

// Parent components can use this interface to ensure they have the properties needed to sync multiple dropdowns
export interface DropdownPillSyncProps {
  isExpanded: boolean
  id: string
  onClick: (id: string | null) => void
}

interface DropdownPillBaseProps {
  labelText: string
  children?: React.ReactNode
  canClickOnContent?: boolean
  style?: string
}

type DropdownPillProps = DropdownPillBaseProps & Partial<DropdownPillSyncProps>

const toExpandIcon = <i className='fa-solid fa-chevron-down' />
const toCollapseIcon = <i className='fa-solid fa-chevron-up' />

/**
 * Button that expands or hides its children
 * @param id Identify the element to parent handlers
 * @param labelText Label for the Button
 * @param children The elements to show or hide
 * @param onClick Passes the component id as an argument
 * @param canClickOnContent Flag to indicate whether clicking on child content should trigger click handler
 * @param isExpanded Flag to inedicate whether content should be displayed or not
 * @param style Class to apply to container
 */
export const DropdownPill: React.FC<DropdownPillProps> = ({
  id = null,
  labelText,
  children,
  onClick = () => {},
  canClickOnContent = true,
  isExpanded = false,
  style
}) => {
  const icon = isExpanded ? toCollapseIcon : toExpandIcon

  const handleClick = (): void => {
    onClick(id)
  }

  const contentToggle = (): void => {
    if (canClickOnContent) {
      handleClick()
    }
  }

  const dropdownContent = isExpanded
    ? <div onClick={contentToggle}>{children}</div>
    : <div className='hidden'>{children}</div>

  return (
    <div className={style}>
      <button className='dropdown-pill-button' onClick={handleClick}>{labelText}{icon}</button>
      <div className='dropdown-pill-children'>
        {dropdownContent}
      </div>
    </div>
  )
}
